import { LocationContact } from '@hakimo-ui/hakimo/types';
import {
  MAX_IMAGES,
  MAX_SMS_LENGTH,
  MAX_TOTAL_BYTES,
  RECOMMENDED_IMAGES,
} from './constants';
import { SMSFailure } from './useSMSFanout';

export interface SMSAlert {
  type: 'error' | 'warning' | 'info';
  message: string;
}

export const canReceiveSms = (c: LocationContact): boolean =>
  !!c.phoneNumber && c.phoneNumber !== '-' && c.tag !== 'Rspndr';

export const visibleLength = (val: string): number =>
  val.replaceAll('\n', '').length;

export const sendLabel = (isRetry: boolean, count: number): string =>
  isRetry ? `Retry (${count})` : count > 1 ? `Send to ${count}` : 'Send';

export const failureAlerts = (failures: SMSFailure[]): SMSAlert[] =>
  failures.map((f) => ({
    type: 'error',
    message: `Failed to send to ${f.contact.name}: ${f.error.message}`,
  }));

export const lengthAlerts = (message: string): SMSAlert[] =>
  visibleLength(message) > MAX_SMS_LENGTH
    ? [
        {
          type: 'error',
          message: `SMS text character limit is ${MAX_SMS_LENGTH}.`,
        },
      ]
    : [];

export const smsAlerts = (
  failures: SMSFailure[],
  files: File[],
  fileError: string | null,
  message: string
): SMSAlert[] => [
  ...failureAlerts(failures),
  ...lengthAlerts(message),
  ...attachmentAlerts(files, fileError),
];

export interface AttachmentState {
  totalBytes: number;
  overBytes: boolean;
  overImages: boolean;
}

export const attachmentState = (files: File[]): AttachmentState => {
  const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
  return {
    totalBytes,
    overBytes: totalBytes > MAX_TOTAL_BYTES,
    overImages: files.length > MAX_IMAGES,
  };
};

export const attachmentAlerts = (
  files: File[],
  fileError: string | null
): SMSAlert[] => {
  const { overBytes, overImages } = attachmentState(files);
  const alerts: SMSAlert[] = [];
  if (fileError) alerts.push({ type: 'warning', message: fileError });
  if (overImages) {
    alerts.push({
      type: 'warning',
      message: `You can attach up to ${MAX_IMAGES} images.`,
    });
  }
  if (overBytes) {
    alerts.push({
      type: 'warning',
      message: 'Total attachments exceed 5 MB — remove one.',
    });
  }
  if (files.length > RECOMMENDED_IMAGES && !overImages) {
    alerts.push({
      type: 'info',
      message: 'More images may be slow or dropped by the carrier.',
    });
  }
  return alerts;
};
